import React, {
    useEffect,
    useState,
    useCallback,
} from "react";

import {
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    Chip,
    Divider,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";

import {
    Search,
    School,
    CalendarMonth,
    PeopleAlt,
    History,
    ArrowUpward,
    Refresh,
    FilterAlt,
    PersonOutline,
    Groups,
    VerifiedOutlined,
    AutoAwesome,
} from "@mui/icons-material";

import Swal from "sweetalert2";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import PromotionStudentRecord from "./PromotionStudentRecord";
import PromotionHistoryPDF from "./PromotionHistoryPDF";


const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    black: "#0A0F12",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C07500",
    background: "#F3F6FB",
    white: "#FFFFFF",
    text: "#243447",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#15803D",
    danger: "#DC2626",
};


const PromotionDashboard = () => {
    const { selectedYear } = useAcademicYear();

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [totalStudents, setTotalStudents] = useState(0);

    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [openPromotion, setOpenPromotion] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("success");


    // =========================================
    // LOAD STUDENTS
    // Existing API endpoint and server pagination
    // =========================================

    const fetchStudents = useCallback(
        async (
            page = 1,
            size = pageSize,
            keyword = search
        ) => {
            if (!selectedYear?.id) {
                setStudents([]);
                setTotalStudents(0);
                setLoading(false);
                return;
            }

            setLoading(true);

            try {
                const params = {
                    page,
                    page_size: size,
                    academic_year: selectedYear.id,
                };

                if (keyword.trim()) {
                    params.search = keyword.trim();
                }

                const response = await api.get(
                    "/api/users/students/",
                    { params }
                );

                const data = response.data.results || [];

                setStudents(data);
                setTotalStudents(
                    response.data.count ?? data.length
                );

                setCurrentPage(
                    Math.max(
                        0,
                        Number(response.data.current_page || page) - 1
                    )
                );
            } catch (error) {
                console.error("Loading students error:", error);

                setStudents([]);
                setTotalStudents(0);

                setAlertMessage(
                    error.response?.data?.detail ||
                    "Unable to load students. Please try again."
                );

                setAlertSeverity("error");
                setSnackbarOpen(true);
            } finally {
                setLoading(false);
            }
        },
        [pageSize, search, selectedYear]
    );


    // =========================================
    // INITIAL LOAD AND ACADEMIC YEAR CHANGES
    // =========================================

    useEffect(() => {
        if (!selectedYear?.id) {
            setStudents([]);
            setTotalStudents(0);
            setCurrentPage(0);
            return;
        }

        fetchStudents(1, pageSize, search);
    }, [selectedYear?.id]);


    // =========================================
    // SEARCH WITH DEBOUNCING
    // =========================================

    useEffect(() => {
        if (!selectedYear?.id) return;

        const timer = setTimeout(() => {
            fetchStudents(1, pageSize, search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search, selectedYear?.id]);


    // =========================================
    // REFRESH AFTER PROMOTION
    // =========================================

    const refreshStudents = () => {
        fetchStudents(
            currentPage + 1,
            pageSize,
            search
        );
    };


    // =========================================
    // TABLE PAGINATION
    // =========================================

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);

        fetchStudents(
            newPage + 1,
            pageSize,
            search
        );
    };


    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);

        setPageSize(newSize);
        setCurrentPage(0);

        fetchStudents(1, newSize, search);
    };


    // =========================================
    // OPEN PROMOTION DIALOG
    // =========================================

    const openPromotionModal = (student) => {
        setSelectedStudent(student);
        setOpenPromotion(true);
    };


    // =========================================
    // OPEN STUDENT PROMOTION HISTORY
    // =========================================

    const openHistoryModal = async (student) => {
        try {
            const studentId = student.user_id;

            if (!studentId) {
                Swal.fire({
                    icon: "warning",
                    title: "Student ID unavailable",
                    text: "Unable to retrieve this student's promotion history.",
                    confirmButtonColor: COLORS.navy,
                });

                return;
            }

            const response = await api.get(
                `/student-promotion-history/${studentId}/`
            );

            setSelectedStudent({
                ...student,
                promotion_history: response.data,
            });

            setOpenHistory(true);
        } catch (error) {
            console.error(
                "Promotion history error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Unable to load history",
                text:
                    error.response?.data?.detail ||
                    "The student's promotion history could not be retrieved.",
                confirmButtonColor: COLORS.navy,
            });
        }
    };


    // =========================================
    // INPUT STYLING
    // =========================================

    const filterInputSx = {
        "& .MuiOutlinedInput-root": {
            minHeight: 48,
            borderRadius: "10px",
            bgcolor: COLORS.white,

            "& fieldset": {
                borderColor: COLORS.border,
            },

            "&:hover fieldset": {
                borderColor: COLORS.deepBlue,
            },

            "&.Mui-focused fieldset": {
                borderColor: COLORS.goldenYellow,
                borderWidth: 2,
            },
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color: COLORS.deepBlue,
        },

        "& .MuiInputBase-input": {
            fontSize: "13px",
        },
    };


    // =========================================
    // TABLE CELL STYLING
    // =========================================

    const headerCellSx = {
        bgcolor: COLORS.navy,
        color: COLORS.white,
        fontSize: "11px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        whiteSpace: "nowrap",
        py: 2,
        px: 2,
        borderBottom: `3px solid ${COLORS.gold}`,
    };


    const bodyCellSx = {
        color: COLORS.text,
        fontSize: "12.5px",
        py: 1.65,
        px: 2,
        whiteSpace: "nowrap",
        borderBottom: `1px solid ${COLORS.border}`,
    };


    // =========================================
    // PAGE UI
    // =========================================

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                boxSizing: "border-box",
                bgcolor: COLORS.background,

                p: {
                    xs: 1.25,
                    sm: 2,
                    md: 3,
                    lg: 3.5,
                },

                "& *": {
                    boxSizing: "border-box",
                },
            }}
        >
            {/* =====================================
                PREMIUM PAGE HEADER
            ===================================== */}

            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    mb: 2.5,

                    borderRadius: {
                        xs: "14px",
                        md: "20px",
                    },

                    background: `
                        linear-gradient(
                            120deg,
                            ${COLORS.navy} 0%,
                            ${COLORS.deepBlue} 72%,
                            #0A4978 100%
                        )
                    `,

                    color: COLORS.white,
                    border: "1px solid rgba(255,255,255,0.08)",
                }}
            >
                {/* Decorative circle */}

                <Box
                    sx={{
                        position: "absolute",
                        right: { xs: -65, md: -20 },
                        top: { xs: -90, md: -125 },

                        width: { xs: 190, md: 310 },
                        height: { xs: 190, md: 310 },

                        borderRadius: "50%",
                        border: "35px solid rgba(246,201,68,0.10)",

                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        right: { xs: 35, md: 120 },
                        bottom: -110,

                        width: 190,
                        height: 190,

                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.035)",

                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,

                        p: {
                            xs: 2.25,
                            sm: 3,
                            md: 3.5,
                        },
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                        justifyContent="space-between"
                        spacing={2.5}
                    >
                        {/* TITLE */}

                        <Stack
                            direction="row"
                            spacing={1.75}
                            alignItems="center"
                            sx={{
                                minWidth: 0,
                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: {
                                        xs: 48,
                                        md: 58,
                                    },

                                    height: {
                                        xs: 48,
                                        md: 58,
                                    },

                                    flexShrink: 0,
                                    borderRadius: "15px",

                                    bgcolor: COLORS.gold,
                                    color: COLORS.navy,

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    boxShadow:
                                        "0 7px 22px rgba(0,0,0,0.15)",
                                }}
                            >
                                <School
                                    sx={{
                                        fontSize: {
                                            xs: 28,
                                            md: 34,
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: "20px",
                                            sm: "24px",
                                            md: "29px",
                                        },

                                        lineHeight: 1.2,
                                        fontWeight: 850,
                                        letterSpacing: "-0.65px",
                                    }}
                                >
                                    Student Promotion
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.75,

                                        color: "rgba(255,255,255,0.76)",

                                        fontSize: {
                                            xs: "11px",
                                            sm: "12px",
                                            md: "13px",
                                        },

                                        lineHeight: 1.7,
                                        maxWidth: 520,
                                    }}
                                >
                                    Manage student progression, review
                                    promotion history, and advance students
                                    into the next academic year.
                                </Typography>
                            </Box>
                        </Stack>


                        {/* ACADEMIC YEAR AND COUNT */}

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,

                                    px: 1.5,
                                    py: 1.15,

                                    borderRadius: "11px",

                                    bgcolor: "rgba(255,255,255,0.10)",

                                    border:
                                        "1px solid rgba(255,255,255,0.15)",

                                    minWidth: 0,
                                    flex: {
                                        xs: 1,
                                        sm: "initial",
                                    },
                                }}
                            >
                                <CalendarMonth
                                    sx={{
                                        color: COLORS.gold,
                                        fontSize: 19,
                                        flexShrink: 0,
                                    }}
                                />

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "9px",
                                            color: "rgba(255,255,255,0.65)",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.9px",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Academic Year
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 800,

                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {selectedYear?.name ||
                                            selectedYear?.year ||
                                            selectedYear?.academic_year ||
                                            "Not selected"}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1.15,

                                    borderRadius: "11px",
                                    bgcolor: COLORS.gold,
                                    color: COLORS.navy,

                                    minWidth: 85,
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "9px",
                                        fontWeight: 800,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.7px",
                                    }}
                                >
                                    Students
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "21px",
                                        lineHeight: 1.25,
                                        fontWeight: 900,
                                    }}
                                >
                                    {totalStudents.toLocaleString()}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>


                    <Divider
                        sx={{
                            my: 2.5,
                            borderColor: "rgba(255,255,255,0.13)",
                        }}
                    />


                    {/* HEADER BADGES */}

                    <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                    >
                        <Chip
                            icon={
                                <Groups
                                    sx={{
                                        color: `${COLORS.gold} !important`,
                                        fontSize: "17px !important",
                                    }}
                                />
                            }
                            label="Student Records"
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                fontWeight: 700,
                                fontSize: "11px",
                                borderRadius: "7px",
                            }}
                        />

                        <Chip
                            icon={
                                <VerifiedOutlined
                                    sx={{
                                        color: "#86EFAC !important",
                                        fontSize: "16px !important",
                                    }}
                                />
                            }
                            label="Academic Progression"
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                fontWeight: 700,
                                fontSize: "11px",
                                borderRadius: "7px",
                            }}
                        />

                        <Typography
                            sx={{
                                ml: {
                                    xs: 0,
                                    sm: "auto",
                                },

                                color: "rgba(255,255,255,0.67)",
                                fontSize: "10px",
                                fontWeight: 600,
                            }}
                        >
                            Promotion Management
                        </Typography>
                    </Stack>
                </Box>
            </Paper>


            {/* =====================================
                SEARCH AND CONTROLS
            ===================================== */}

            <Paper
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: COLORS.white,
                    mb: 2.5,
                    overflow: "hidden",
                }}
            >
                {/* FILTER HEADER */}

                <Box
                    sx={{
                        px: { xs: 2, md: 2.5 },
                        py: 2,

                        borderBottom: `1px solid ${COLORS.border}`,

                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                    }}
                >
                    <Box
                        sx={{
                            width: 38,
                            height: 38,

                            borderRadius: "10px",
                            bgcolor: "#EFF6FF",
                            color: COLORS.deepBlue,

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            flexShrink: 0,
                        }}
                    >
                        <FilterAlt />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                color: COLORS.navy,
                                fontWeight: 850,
                                fontSize: "15px",
                            }}
                        >
                            Student Records &amp; Search
                        </Typography>

                        <Typography
                            sx={{
                                color: COLORS.muted,
                                fontSize: "11px",
                                mt: 0.35,
                            }}
                        >
                            Find students, review their history,
                            and initiate academic promotion.
                        </Typography>
                    </Box>

                    <Tooltip title="Refresh student records">
                        <span>
                            <IconButton
                                onClick={() =>
                                    fetchStudents(
                                        currentPage + 1,
                                        pageSize,
                                        search
                                    )
                                }
                                disabled={
                                    loading || !selectedYear?.id
                                }
                                sx={{
                                    color: COLORS.deepBlue,
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: "10px",
                                    width: 40,
                                    height: 40,
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={19} />
                                ) : (
                                    <Refresh />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>


                {/* SEARCH FIELD */}

                <Box
                    sx={{
                        p: {
                            xs: 2,
                            md: 2.5,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",

                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                sm: "minmax(0, 1fr) auto",
                            },

                            gap: 1.5,
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Search student records"
                            placeholder="Search by student name..."
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setCurrentPage(0);
                            }}
                            sx={filterInputSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search
                                            sx={{
                                                color: COLORS.muted,
                                                fontSize: 21,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => {
                                setSearch("");
                                setCurrentPage(0);

                                fetchStudents(
                                    1,
                                    pageSize,
                                    ""
                                );
                            }}
                            disabled={loading || !selectedYear?.id}
                            sx={{
                                minHeight: 48,
                                px: 2.5,
                                borderRadius: "10px",
                                borderColor: COLORS.border,
                                color: COLORS.deepBlue,
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 800,
                                whiteSpace: "nowrap",

                                "&:hover": {
                                    bgcolor: "#EFF6FF",
                                    borderColor: COLORS.deepBlue,
                                },

                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                            }}
                        >
                            Clear Search
                        </Button>
                    </Box>


                    {/* SEARCH STATUS */}

                    <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ mt: 1.75 }}
                    >
                        <Chip
                            size="small"
                            icon={<PeopleAlt />}
                            label={`${students.length} displayed`}
                            sx={{
                                height: 27,
                                fontSize: "11px",
                                fontWeight: 750,
                                bgcolor: "#EFF6FF",
                                color: COLORS.deepBlue,
                                borderRadius: "7px",

                                "& .MuiChip-icon": {
                                    color: COLORS.deepBlue,
                                    fontSize: 16,
                                },
                            }}
                        />

                        <Chip
                            size="small"
                            icon={<CalendarMonth />}
                            label={
                                selectedYear?.name ||
                                selectedYear?.year ||
                                selectedYear?.academic_year ||
                                "No academic year"
                            }
                            sx={{
                                height: 27,
                                fontSize: "11px",
                                fontWeight: 750,
                                bgcolor: "#FFF7DB",
                                color: COLORS.orange,
                                borderRadius: "7px",

                                "& .MuiChip-icon": {
                                    color: COLORS.orange,
                                    fontSize: 16,
                                },
                            }}
                        />

                        {search.trim() && (
                            <Typography
                                sx={{
                                    ml: {
                                        xs: 0,
                                        sm: "auto",
                                    },
                                    color: COLORS.muted,
                                    fontSize: "11px",
                                }}
                            >
                                Search: "{search.trim()}"
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </Paper>


            {/* =====================================
                STUDENT TABLE
            ===================================== */}

            <Paper
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${COLORS.border}`,
                    overflow: "hidden",
                    bgcolor: COLORS.white,
                }}
            >
                {/* TABLE TITLE */}

                <Box
                    sx={{
                        p: {
                            xs: 2,
                            md: 2.5,
                        },

                        display: "flex",

                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },

                        alignItems: {
                            xs: "flex-start",
                            sm: "center",
                        },

                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,

                                borderRadius: "11px",
                                bgcolor: "#FFF7DB",
                                color: COLORS.orange,

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <PeopleAlt />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    color: COLORS.navy,
                                    fontSize: "16px",
                                    fontWeight: 850,
                                }}
                            >
                                Registered Students
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: COLORS.muted,
                                    fontSize: "11px",
                                }}
                            >
                                Student details, current classes,
                                promotion history, and actions.
                            </Typography>
                        </Box>
                    </Stack>


                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        alignItems="center"
                        gap={1}
                    >
                        <Chip
                            size="small"
                            label={`${totalStudents.toLocaleString()} total students`}
                            sx={{
                                fontWeight: 750,
                                fontSize: "11px",
                                bgcolor: "#EFF6FF",
                                color: COLORS.deepBlue,
                                borderRadius: "7px",
                            }}
                        />
                    </Stack>
                </Box>


                <Divider
                    sx={{
                        borderColor: COLORS.border,
                    }}
                />


                {/* TABLE */}

                <TableContainer
                    sx={{
                        width: "100%",
                        overflowX: "auto",
                        position: "relative",

                        maxHeight: {
                            xs: "none",
                            md: "calc(100vh - 400px)",
                        },

                        minHeight: 220,

                        "&::-webkit-scrollbar": {
                            height: 8,
                            width: 8,
                        },

                        "&::-webkit-scrollbar-thumb": {
                            bgcolor: "#CBD5E1",
                            borderRadius: 10,
                        },

                        "&::-webkit-scrollbar-track": {
                            bgcolor: "#F1F5F9",
                        },
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="Student promotion records"
                        sx={{
                            minWidth: 900,
                            tableLayout: "auto",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {[
                                    "Student",
                                    "Current Class",
                                    "Division",
                                    "Promotion History",
                                    "Action",
                                ].map((heading) => (
                                    <TableCell
                                        key={heading}
                                        sx={headerCellSx}
                                    >
                                        {heading}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>


                        <TableBody>
                            {!selectedYear?.id ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        sx={{ py: 8 }}
                                    >
                                        <CalendarMonth
                                            sx={{
                                                fontSize: 42,
                                                color: COLORS.muted,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                mt: 1,
                                                fontWeight: 800,
                                                color: COLORS.navy,
                                            }}
                                        >
                                            Select an academic year
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                color: COLORS.muted,
                                            }}
                                        >
                                            Choose an academic year to
                                            load its registered students.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : loading && students.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        sx={{ py: 8 }}
                                    >
                                        <CircularProgress
                                            size={34}
                                            sx={{
                                                color: COLORS.deepBlue,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                mt: 1.5,
                                                color: COLORS.muted,
                                                fontSize: "12px",
                                            }}
                                        >
                                            Loading student records...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : students.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        sx={{ py: 8 }}
                                    >
                                        <Box
                                            sx={{
                                                mx: "auto",
                                                width: 58,
                                                height: 58,

                                                borderRadius: "16px",
                                                bgcolor: "#F1F5F9",

                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",

                                                color: COLORS.muted,
                                            }}
                                        >
                                            <PersonOutline
                                                sx={{
                                                    fontSize: 30,
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                mt: 1.5,
                                                fontWeight: 850,
                                                fontSize: "15px",
                                                color: COLORS.navy,
                                            }}
                                        >
                                            No students found
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                color: COLORS.muted,
                                            }}
                                        >
                                            {search.trim()
                                                ? "Try another student name or clear your search."
                                                : "Registered students will appear here when available."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                students.map((record, index) => (
                                    <TableRow
                                        key={
                                            record.user_id ||
                                            record.id
                                        }
                                        hover
                                        sx={{
                                            bgcolor:
                                                index % 2 === 0
                                                    ? COLORS.white
                                                    : "#FAFCFF",

                                            transition:
                                                "background-color 0.18s ease",

                                            "&:hover": {
                                                bgcolor:
                                                    "#FFF9E8 !important",
                                            },

                                            "& td:first-of-type": {
                                                borderLeft:
                                                    "3px solid transparent",
                                            },
                                        }}
                                    >
                                        {/* STUDENT */}

                                        <TableCell sx={bodyCellSx}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1.25}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,

                                                        borderRadius: "10px",
                                                        bgcolor: "#EAF1F9",
                                                        color: COLORS.deepBlue,

                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",

                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <PersonOutline
                                                        sx={{
                                                            fontSize: 21,
                                                        }}
                                                    />
                                                </Box>

                                                <Box
                                                    sx={{
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: COLORS.navy,
                                                        }}
                                                    >
                                                        {record.full_name ||
                                                            "Unnamed Student"}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: "10px",
                                                            mt: 0.3,
                                                            color: COLORS.muted,
                                                        }}
                                                    >
                                                        Student record
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>


                                        {/* CURRENT CLASS */}

                                        <TableCell sx={bodyCellSx}>
                                            <Chip
                                                icon={
                                                    <School
                                                        sx={{
                                                            fontSize:
                                                                "15px !important",
                                                        }}
                                                    />
                                                }
                                                label={
                                                    record.grade_class_name ||
                                                    "Not assigned"
                                                }
                                                size="small"
                                                sx={{
                                                    height: 28,
                                                    maxWidth: 220,

                                                    bgcolor: "#EFF6FF",
                                                    color: COLORS.deepBlue,

                                                    fontSize: "11px",
                                                    fontWeight: 750,
                                                    borderRadius: "7px",

                                                    "& .MuiChip-icon": {
                                                        color: COLORS.deepBlue,
                                                    },

                                                    "& .MuiChip-label": {
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    },
                                                }}
                                            />
                                        </TableCell>


                                        {/* DIVISION */}

                                        <TableCell sx={bodyCellSx}>
                                            <Typography
                                                sx={{
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    color: COLORS.text,
                                                }}
                                            >
                                                {record.division_assigned_name ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>


                                        {/* HISTORY */}

                                        <TableCell sx={bodyCellSx}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<History />}
                                                onClick={() =>
                                                    openHistoryModal(record)
                                                }
                                                sx={{
                                                    minHeight: 34,
                                                    px: 1.5,

                                                    borderRadius: "8px",
                                                    borderColor: "#CBD5E1",

                                                    color: COLORS.deepBlue,
                                                    textTransform: "none",

                                                    fontSize: "11px",
                                                    fontWeight: 800,
                                                    whiteSpace: "nowrap",

                                                    "&:hover": {
                                                        bgcolor: "#EFF6FF",
                                                        borderColor:
                                                            COLORS.deepBlue,
                                                    },
                                                }}
                                            >
                                                View History
                                            </Button>
                                        </TableCell>


                                        {/* PROMOTION ACTION */}

                                        <TableCell sx={bodyCellSx}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<ArrowUpward />}
                                                onClick={() =>
                                                    openPromotionModal(record)
                                                }
                                                sx={{
                                                    minHeight: 36,
                                                    px: 1.75,

                                                    borderRadius: "8px",
                                                    bgcolor: COLORS.navy,
                                                    color: COLORS.white,

                                                    textTransform: "none",
                                                    fontSize: "11px",
                                                    fontWeight: 800,
                                                    whiteSpace: "nowrap",

                                                    boxShadow: "none",

                                                    "&:hover": {
                                                        bgcolor: COLORS.deepBlue,

                                                        boxShadow:
                                                            "0 5px 14px rgba(6,42,75,0.16)",
                                                    },
                                                }}
                                            >
                                                Promote Student
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>


                {/* =====================================
                    PAGINATION
                ===================================== */}

                <Box
                    sx={{
                        borderTop: `1px solid ${COLORS.border}`,
                        bgcolor: "#FAFCFF",
                        px: {
                            xs: 0.5,
                            sm: 1.5,
                        },
                    }}
                >
                    <TablePagination
                        component="div"
                        count={totalStudents}
                        rowsPerPage={pageSize}
                        page={currentPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[
                            20,
                            50,
                            100,
                            200,
                        ]}
                        labelRowsPerPage="Students:"
                        disabled={loading || !selectedYear?.id}
                        sx={{
                            "& .MuiTablePagination-toolbar": {
                                minHeight: 64,

                                flexWrap: {
                                    xs: "wrap",
                                    sm: "nowrap",
                                },

                                justifyContent: {
                                    xs: "center",
                                    sm: "flex-end",
                                },

                                px: {
                                    xs: 0.5,
                                    sm: 1,
                                },

                                py: 1,
                                gap: 0.5,
                            },

                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                                {
                                    fontSize: "11px",
                                    color: COLORS.muted,
                                    fontWeight: 650,
                                },

                            "& .MuiTablePagination-select": {
                                fontSize: "12px",
                                fontWeight: 750,
                            },

                            "& .MuiTablePagination-actions button": {
                                color: COLORS.deepBlue,
                                borderRadius: "8px",
                            },
                        }}
                    />
                </Box>
            </Paper>


            {/* =====================================
                PROMOTION DIALOG
                Existing component preserved
            ===================================== */}

            <PromotionStudentRecord
                open={openPromotion}
                student={selectedStudent}
                onClose={() => {
                    setOpenPromotion(false);
                    setSelectedStudent(null);
                }}
                refresh={refreshStudents}
            />


            {/* =====================================
                PROMOTION HISTORY / PDF
                Existing component preserved
            ===================================== */}

            <PromotionHistoryPDF
                open={openHistory}
                student={selectedStudent}
                onClose={() => {
                    setOpenHistory(false);
                    setSelectedStudent(null);
                }}
            />


            {/* =====================================
                NOTIFICATIONS
            ===================================== */}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                sx={{
                    width: {
                        xs: "calc(100% - 24px)",
                        sm: "auto",
                    },

                    bottom: {
                        xs: "12px !important",
                        sm: "24px !important",
                    },

                    left: {
                        xs: "12px !important",
                        sm: "auto",
                    },
                }}
            >
                <Alert
                    severity={alertSeverity}
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        fontWeight: 650,
                        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PromotionDashboard;
